import { ChangeEvent, memo } from 'react';
import { Alert, TextField } from '@mui/material';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import { mainWhite } from '@modules/theme';
import { breadcrumb } from '@utils/logging';
import useTeams from '@hooks/useTeams';
import useLatestCallback from 'use-latest-callback';
import { TeamAthlete } from '@type/db-types';
import { useNavigate } from 'react-router';

export const MAX_TEAMS_IN_SIDE_BAR = 5;

const getOptionLabel = (option: TeamAthlete) => option.name;
const getOptionSelected = (option: TeamAthlete, value: TeamAthlete) => value.id === option.id;

const TeamSelector = () => {
    const { selectedTeam, setSelectedTeam, teams } = useTeams();
    const navigate = useNavigate();

    /**
     * Updates the selected team
     * @param {object} team - the selected team
     */
    const onTeamSelect = useLatestCallback((team: TeamAthlete) => {
        // Set selected team jobs, after filter
        breadcrumb('Selecting a new Team', { team });
        setSelectedTeam(team);
        navigate(`/teams/${team.id}`);
    });

    const onChange = useLatestCallback((_event: ChangeEvent<unknown>, selected: TeamAthlete) => {
        if (selected) {
            onTeamSelect(selected);
        }
    });

    const input = useLatestCallback((params: AutocompleteRenderInputParams) => (
        <TextField
            {...params}
            sx={{
                // backgroundColor: secondaryDark
                marginTop: selectedTeam ? 2 : 0,
            }}
            label={selectedTeam ? 'Team' : ''}
            slotProps={{
                input: {
                    ...params.InputProps,
                    sx: {
                        color: mainWhite,
                        border: (theme) =>
                            `1px solid ${selectedTeam ? 'transparent' : theme.palette.primary.main}`,
                        paddingBottom: '0 !important',
                        paddingTop: '0 !important',
                    },
                },
                inputLabel: {
                    sx: { color: mainWhite },
                    shrink: true,
                },
            }}
            margin="dense"
            variant="outlined"
            placeholder="Select a team..."
        />
    ));

    return teams?.length && teams?.length > MAX_TEAMS_IN_SIDE_BAR ? (
        <>
            {selectedTeam ? null : (
                <Alert severity="info" sx={{ marginY: 1, marginX: 1.5 }}>
                    Select a Team to manage
                </Alert>
            )}
            <Autocomplete
                key={selectedTeam ? selectedTeam?.id : 'none'}
                id="team-selector-combo-box"
                disableClearable
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={getOptionSelected}
                onChange={onChange}
                options={teams}
                renderInput={input}
                sx={{ margin: '.5vh auto', width: '95%' }}
                value={selectedTeam || undefined}
            />
        </>
    ) : null;
};

export default memo(TeamSelector);
